import { initializeApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from 'firebase/auth'
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore'
import { initializeAppCheck, ReCaptchaEnterpriseProvider, getToken } from 'firebase/app-check'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const db = getFirestore(app)
export const googleProvider = new GoogleAuthProvider()

// Local dev only -- routes Auth/Firestore to local emulators instead of a
// real project (see client/src/config/firebase.js for the main app's
// equivalent). Must run before any other auth/db call.
if (import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, 'localhost', 8080)
}

// Mirrors client/src/config/firebase.js's App Check setup -- without this,
// the portal never attached an X-Firebase-AppCheck header at all, so
// enforcing App Check on Firestore/Storage/Functions would have broken
// every portal API call and every direct Firestore read (realtime
// messages, etc.) with no client-side wiring to fall back on.
let appCheck = null
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY

if (recaptchaSiteKey) {
  try {
    if (import.meta.env.DEV) {
      // @ts-ignore - Debug token for development
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true
    }

    // Registered key ("Juristack Prod") is a reCAPTCHA Enterprise key --
    // must match client/src/config/firebase.js's provider.
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true
    })
  } catch (error) {
    console.error('App Check initialization failed:', error)
  }
} else {
  console.warn('App Check not initialized: VITE_RECAPTCHA_SITE_KEY is not set.')
}

/**
 * Get App Check token for API requests. Returns null if App Check isn't
 * configured (matches client/src/config/firebase.js's contract).
 */
export const getAppCheckToken = async () => {
  if (!appCheck) return null
  try {
    const tokenResult = await getToken(appCheck, false)
    return tokenResult.token
  } catch (error) {
    console.warn('Failed to get App Check token:', error.message)
    return null
  }
}

export { appCheck }
export default app
